import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchProduct,
  fetchProductFromProduction,
  getBestSellerById,
  getScreensaver,
  getStoreData,
  postProduct
} from '../services/customizerAPI';
import axiosClient from '../services/apiClient';
import AuthManager from '../../utils/AuthManager';
import { IAddFavoriteRequestPayload, IDeleteFavoriteRequestPayload } from '../types/builder';

export interface IGetBestSellerProductPayload {
  id: string;
  data?: {
    size: string | number;
    engraving?: string;
  };
}

export interface IGetScreensaverPayload {
  subpath?: string | null;
}

export const getProductDetail = createAsyncThunk(
  'GET_PRODUCT_DETAIL_ACTION_TYPES',
  async ({ url, type = 1 }: { url: string; type: number }) => {
    const isStagingApi = (process.env.REACT_APP_API_BASE_URL || '').includes('staging');
    const productionShippingPromise = isStagingApi
      ? fetchProductFromProduction(url, type).catch(() => null)
      : Promise.resolve(null);
    const response = await fetchProduct(url, type);
    const data = response?.data;
    const productionResponse = await productionShippingPromise;
    const productionShipping = productionResponse?.data?.shipping_options;
    if (data && productionShipping) {
      return { ...data, shipping_options: productionShipping };
    }
    return data;
  }
);

export const createProduct = createAsyncThunk(
  'CREATE_PRODUCT_ACTION_TYPES',
  async ({ data, selectedShippingPriority }: { data: any; selectedShippingPriority?: string }) => {
    const response = await postProduct(data, selectedShippingPriority);
    return response?.data;
  }
);

export const getBestSellerProduct = createAsyncThunk<any, IGetBestSellerProductPayload>(
  'lashbrook/getBestSellerProduct',
  async (payload, { fulfillWithValue, rejectWithValue }) => {
    try {
      const res = await getBestSellerById(payload.id, payload.data);
      return fulfillWithValue(res.data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getScreensaverData = createAsyncThunk('GET_SCREENSAVER_DATA', async () => {
  const response = await getScreensaver();
  return response?.data;
});

export const getStoreDataAction = createAsyncThunk('GET_STORE_DATA', async (subpath?: string) => {
  const response = await getStoreData(subpath);
  return response?.data;
});

const serializeAxiosError = (error: any) => ({
  status: error?.response?.status,
  message: error?.message
});

export const addFavourite = createAsyncThunk(
  'lashbrook/addFavorite',
  async (data: IAddFavoriteRequestPayload, { fulfillWithValue, rejectWithValue }) => {
    try {
      const userId = AuthManager.getAnonymousUserId();
      data.user_id = userId;
      const res = await axiosClient.post('/v1/favorites', data);
      return fulfillWithValue(res.data);
    } catch (error) {
      return rejectWithValue(serializeAxiosError(error));
    }
  }
);

export const getFavourites = createAsyncThunk(
  'lashbrook/getFavorites',
  async (_, { fulfillWithValue, rejectWithValue }) => {
    try {
      const userId = AuthManager.getAnonymousUserId();
      const res = await axiosClient.get(`/v1/favorites${userId ? `?user_id=${userId}` : ''}`);
      return fulfillWithValue(res.data);
    } catch (error) {
      return rejectWithValue(serializeAxiosError(error));
    }
  }
);

export const deleteFavourite = createAsyncThunk(
  'lashbrook/deleteFavorite',
  async (data: IDeleteFavoriteRequestPayload, { fulfillWithValue, rejectWithValue }) => {
    try {
      const userId = AuthManager.getAnonymousUserId();
      const res = await axiosClient.delete(`/v1/favorites${userId ? `?user_id=${userId}` : ''}`, { data });
      return fulfillWithValue(res.data);
    } catch (error) {
      return rejectWithValue(serializeAxiosError(error));
    }
  }
);
